import { DayOfWeek } from 'models/DayOfWeek';
import { LeadChannel } from 'models/Leads';
import { BaseErrorResponse, BaseResponse } from 'models/_base';

export enum RecipientType {
  AGENT = 'AGENT',
  BOOKING_AGENT = 'BOOKING_AGENT',
  COPY_AGENCY = 'COPY_AGENCY',
  EMPLOYEE = 'EMPLOYEE',
  GUEST = 'GUEST',
  OWNER = 'OWNER',
  SERVICE_PROVIDER = 'SERVICE_PROVIDER',
  UNKNOWN = 'UNKNOWN',
  USER = 'USER',
}

export interface TriggerRecipient {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  recipientUid?: string | null;
  triggerUid?: string;
  type: RecipientType;
  uid?: string | null;
}

export interface Trigger {
  actionType: TriggerActionType;
  autoSelectTemplateVersionForRecipient: boolean;
  condition: TriggerCondition | null;
  eventType: TriggerEventType;
  isActive: boolean;
  jobTiming: TriggerJobTiming | null;
  name: string;
  recipients: TriggerRecipient[] | null;
  serviceUid: string | null;
  templateName: string | null;
  templateUid: string | null;
  triggerType: TriggerType;
  uid: string;
}

export enum TriggerActionType {
  CREATE_JOB = 'CREATE_JOB',
  CREATE_REVIEW = 'CREATE_REVIEW',
  DIRECT_MESSAGE = 'DIRECT_MESSAGE',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
}

export enum TriggerEventType {
  AFTER_STAY_ENDS = 'AFTER_STAY_ENDS',
  AFTER_STAY_STARTS = 'AFTER_STAY_STARTS',
  BEFORE_STAY_ENDS = 'BEFORE_STAY_ENDS',
  BEFORE_STAY_STARTS = 'BEFORE_STAY_STARTS',
  BLOCK_DELETED = 'BLOCK_DELETED',
  BLOCK_UPDATED = 'BLOCK_UPDATED',
  BOOKING_CANCELLED = 'BOOKING_CANCELLED',
  BOOKING_PAID_IN_FULL = 'BOOKING_PAID_IN_FULL',
  BOOKING_UPDATED = 'BOOKING_UPDATED',
  COMPLEX_CONDITION = 'COMPLEX_CONDITION',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  JOB_ACCEPTED = 'JOB_ACCEPTED',
  JOB_CANCELLED = 'JOB_CANCELLED',
  JOB_COMPLETED = 'JOB_COMPLETED',
  JOB_CREATED = 'JOB_CREATED',
  JOB_DECLINED = 'JOB_DECLINED',
  LEAD_DATES_CHANGED = 'LEAD_DATES_CHANGED',
  LEAD_PROPERTY_CHANGED = 'LEAD_PROPERTY_CHANGED',
  NEW_BLOCKED_DATES = 'NEW_BLOCKED_DATES',
  NEW_BOOKING = 'NEW_BOOKING',
  NEW_BOOKING_REQUEST = 'NEW_BOOKING_REQUEST',
  NEW_INBOX_MESSAGE = 'NEW_INBOX_MESSAGE',
  NEW_INQUIRY = 'NEW_INQUIRY',
  NEW_PROPERTY = 'NEW_PROPERTY',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  PRE_ARRIVAL_FORM_COMPLETED = 'PRE_ARRIVAL_FORM_COMPLETED',
  RENTAL_AGREEMENT_SIGNED = 'RENTAL_AGREEMENT_SIGNED',
  UNIT_CHANGED = 'UNIT_CHANGED',
  UPDATED_PROPERTY = 'UPDATED_PROPERTY',
}

export enum TriggerJobTiming {
  AT_CHECK_IN = 'AT_CHECK_IN',
  AT_CHECK_OUT = 'AT_CHECK_OUT',
  CHECK_IN_CHECK_OUT = 'CHECK_IN_CHECK_OUT',
}

export enum TriggerLogType {
  FAILURE = 'FAILURE',
  SUCCESS_NOT_SENT = 'SUCCESS_NOT_SENT',
  SUCCESS_SENT = 'SUCCESS_SENT',
}

export enum TriggerState {
  CANCELLED = 'CANCELLED',
  DELETED_BY_PM = 'DELETED_BY_PM',
  FAILED = 'FAILED',
  FIRED_SUCCESSFULLY = 'FIRED_SUCCESSFULLY',
  SCHEDULED = 'SCHEDULED',
}

export enum TriggerType {
  EVENT_BASED = 'EVENT_BASED',
  TIME_BASED = 'TIME_BASED',
}

export enum TriggerTimeCondition {
  CHECK_IN = 'CHECK_IN',
  CHECK_OUT = 'CHECK_OUT',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
}

export interface GetTriggersResponseTO extends BaseResponse {
  triggers: Trigger[];
}

export interface GetTriggerResponseTO {
  trigger: Trigger;
}

export interface TemplateOption {
  name: string;
  uid: string;
  childTemplatesCount: number;
}

export interface RecipientOption {
  type: RecipientType;
  uid: string | null;
  name: string;
  phoneNumber: string | null;
  hasEmail: boolean;
  hasPhoneNumber: boolean;
}

export interface ServiceOption {
  name: string;
  uid: string;
}

export interface TriggerOptions {
  actions: TriggerActionType[];
  emailTemplates: TemplateOption[];
  recipients: RecipientOption[];
  services: ServiceOption[];
  smsTemplates: TemplateOption[];
  whatsAppTemplates: TemplateOption[];
}

export interface GetTriggerOptionsResponseTO {
  option: TriggerOptions;
}

export enum ConditionType {
  AFTER_STAY_ENDS = 'AFTER_STAY_ENDS',
  AFTER_STAY_STARTS = 'AFTER_STAY_STARTS',
  AGREEMENT_SIGNED = 'AGREEMENT_SIGNED',
  AND = 'AND',
  BEFORE_STAY_ENDS = 'BEFORE_STAY_ENDS',
  BEFORE_STAY_STARTS = 'BEFORE_STAY_STARTS',
  BOOKING_NOT_PAID_IN_FULL = 'BOOKING_NOT_PAID_IN_FULL',
  BOOKING_PAID_IN_FULL = 'BOOKING_PAID_IN_FULL',
  CHANNEL = 'CHANNEL',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  GUESTS_NUMBER = 'GUESTS_NUMBER',
  NEGATION = 'NEGATION',
  OR = 'OR',
  PRE_ARRIVAL_FORM_SUBMITTED = 'PRE_ARRIVAL_FORM_SUBMITTED',
  PROPERTY = 'PROPERTY',
  STAY_IS_ACTIVE = 'STAY_IS_ACTIVE',
  STAY_LENGTH = 'STAY_LENGTH',
}

export enum TimeOperator {
  LESS_THAN_OR_EQUAL = 'LESS_THAN_OR_EQUAL',
  EQUAL = 'EQUAL',
  GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL',
}

export enum GuestsNumberOperator {
  IS_GREATER_THAN = 'IS_GREATER_THAN',
  IS_LOWER_THAN = 'IS_LOWER_THAN',
}

export enum StayLengthOperator {
  IS_LONGER = 'IS_LONGER',
  IS_SHORTER = 'IS_SHORTER',
}

export enum IsSigned {
  IS_SIGNED = 'IS_SIGNED',
  IS_NOT_SIGNED = 'IS_NOT_SIGNED',
}

export enum IsCompleted {
  IS_COMPLETED = 'IS_COMPLETED',
  IS_NOT_COMPLETED = 'IS_NOT_COMPLETED',
}

export enum TimeType {
  DAYS = 'DAYS',
  HOURS = 'HOURS',
  MINUTES = 'MINUTES',
}

export interface TriggerConditionForChannel {
  all: boolean;
  channels: LeadChannel[];
  conditionType: ConditionType.CHANNEL;
}

export interface TriggerConditionForDaysOfWeek {
  conditionType: ConditionType.DAY_OF_WEEK;
  daysOfWeek: DayOfWeek[];
}

export interface TriggerConditionForGuestNumber {
  conditionType: ConditionType.GUESTS_NUMBER;
  guests: number;
  operator: GuestsNumberOperator;
}

export interface TriggerConditionForNegation {
  condition: { conditionType: ConditionType };
  conditionType: ConditionType.NEGATION;
}

export interface TriggerConditionForProperty {
  all: boolean;
  conditionType: ConditionType.PROPERTY;
  propertyUids: string[];
}

interface TriggerConditionForBeforeStay {
  backupTimeInMilliSeconds?: number;
  conditionType:
    | ConditionType.BEFORE_STAY_ENDS
    | ConditionType.BEFORE_STAY_STARTS;
  operator: TimeOperator;
  timeInMilliSeconds: number;
}

interface TriggerConditionForAfterStay {
  backupTimeInMilliSeconds?: number;
  conditionType:
    | ConditionType.AFTER_STAY_ENDS
    | ConditionType.AFTER_STAY_STARTS;
  operator?: TimeOperator;
  timeInMilliSeconds: number;
}

export type TriggerConditionForStay =
  | TriggerConditionForAfterStay
  | TriggerConditionForBeforeStay;

export interface TriggerConditionForStayLength {
  days: number;
  conditionType: ConditionType.STAY_LENGTH;
  operator: StayLengthOperator;
}

export type AllTriggerConditions =
  | TriggerCondition
  | TriggerConditionForChannel
  | TriggerConditionForDaysOfWeek
  | TriggerConditionForGuestNumber
  | TriggerConditionForNegation
  | TriggerConditionForProperty
  | TriggerConditionForAfterStay
  | TriggerConditionForBeforeStay
  | TriggerConditionForStayLength;

export interface TriggerCondition {
  conditionType: Exclude<
    ConditionType,
    | ConditionType.AFTER_STAY_ENDS
    | ConditionType.AFTER_STAY_STARTS
    | ConditionType.BEFORE_STAY_ENDS
    | ConditionType.BEFORE_STAY_STARTS
    | ConditionType.CHANNEL
    | ConditionType.DAY_OF_WEEK
    | ConditionType.GUESTS_NUMBER
    | ConditionType.NEGATION
    | ConditionType.PROPERTY
    | ConditionType.STAY_LENGTH
  >;
  conditions?: AllTriggerConditions[];
}

export interface TriggersCommonPayload {
  actionType: TriggerActionType;
  autoSelectTemplateVersionForRecipient?: boolean;
  condition: TriggerCondition;
  emailBcc?: boolean;
  eventType: TriggerEventType;
  jobTiming?: TriggerJobTiming;
  name: string;
  recipients?: { recipientUid: string | null; type: RecipientType }[];
  serviceUid?: string;
  templateName?: string;
  templateUid?: string;
  triggerType: TriggerType;
}

export interface TriggersCreatePayload extends TriggersCommonPayload {}

export interface TriggersUpdatePayload extends TriggersCommonPayload {
  uid: string;
}

export interface PostTriggersResponseTO extends BaseErrorResponse {}
